/**
 * CustomersTab.tsx - Customer management tab for Admin Dashboard
 * 
 * Features:
 * - Paginated customer list with search
 * - Sample tracking with inline fulfillment actions
 * - Customer stage filtering
 * - Segments overview with visual cards
 * - Last Active date (business-relevant) + Joined date (secondary)
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetch } from '../../services/apiAuth';
import { getApiBaseUrl } from '../../utils/apiConfig';
import './CustomersTab.css';

// Types matching backend API
interface CustomerSummary {
  uid: string;
  name: string | null;
  email: string | null;
  photoURL: string | null;
  skinType: string | null;
  concerns: string[];
  customerStage: string | null;
  lifetimeValueUSD: number;
  purchaseCount: number;
  profileCompletion: number;
  createdAt: string | null;
  lastVisitDate: string | null;
}

interface ChatMessage {
  sender: 'user' | 'bot';
  text: string;
  timestamp: string;
  type?: string;
}

interface ConversationSummary {
  conversationId: string;
  userId: string;
  startedAt: string;
  lastMessageAt: string;
  messageCount: number;
  messages: ChatMessage[];
  userPreview: string;
  topics: string[];
}

interface CustomersTabProps {
  language?: string; // Deprecated: using i18n now
}

type SubTab = 'all' | 'segments';
type StageFilter = 'all' | 'visitor' | 'sample-requested' | 'feedback-given' | 'converted' | 'loyal' | 'churned';

const CustomersTab: React.FC<CustomersTabProps> = () => {
  const { t, i18n } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const language = i18n.language;
  
  const [subTab, setSubTab] = useState<SubTab>('all');
  const [customers, setCustomers] = useState<CustomerSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState<StageFilter>('all');
  const [total, setTotal] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  // Stats for segments view
  const [segmentStats, setSegmentStats] = useState<Record<string, number>>({});
  const [deletingUid, setDeletingUid] = useState<string | null>(null);
  const deleteInflight = useRef<Set<string>>(new Set());

  // Per-user chat log state
  const [chatExpandedUid, setChatExpandedUid] = useState<string | null>(null);
  const [chatConversations, setChatConversations] = useState<ConversationSummary[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [chatExpandedConvId, setChatExpandedConvId] = useState<string | null>(null);
  const chatRequestRef = useRef(0);

  // Per-user profile detail expansion
  const [profileExpandedUid, setProfileExpandedUid] = useState<string | null>(null);

  const toggleUserChat = async (uid: string, forceReload = false) => {
    if (chatExpandedUid === uid && !forceReload) {
      ++chatRequestRef.current;
      setChatExpandedUid(null);
      setChatConversations([]);
      setChatExpandedConvId(null);
      setChatError(null);
      setChatLoading(false);
      return;
    }
    setChatExpandedUid(uid);
    setChatConversations([]);
    setChatExpandedConvId(null);
    setChatError(null);
    setChatLoading(true);
    const requestId = ++chatRequestRef.current;
    try {
      const response = await authFetch(
        `${getApiBaseUrl()}/getChatHistory?limit=50&userId=${encodeURIComponent(uid)}`
      );
      if (requestId !== chatRequestRef.current) return; // stale response
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setChatConversations(result.data.conversations);
        } else {
          setChatError(t('customers.chatLoadFailed', 'Failed to load conversations'));
        }
      } else {
        setChatError(t('customers.chatLoadFailed', 'Failed to load conversations'));
      }
    } catch (err) {
      if (requestId !== chatRequestRef.current) return;
      console.error('Failed to load user chat:', err);
      setChatError(t('customers.chatLoadFailed', 'Failed to load conversations'));
    } finally {
      if (requestId === chatRequestRef.current) {
        setChatLoading(false);
      }
    }
  };

  const formatChatTimestamp = (ts: string) => {
    if (!ts) return '—';
    try {
      return new Date(ts).toLocaleDateString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return ts; }
  };

  const handleDeleteUser = async (uid: string, name: string | null, email: string | null) => {
    const label = name || email || uid;
    const confirmed = window.confirm(
      `⚠️ PERMANENTLY delete user "${label}"?\n\nThis removes ALL Firestore data, chat history, orders, and Firebase Auth. This cannot be undone.`
    );
    if (!confirmed) return;

    const doubleConfirm = window.prompt(
      'Type DELETE THIS USER to confirm:'
    );
    if (doubleConfirm !== 'DELETE THIS USER') return;

    if (deleteInflight.current.has(uid)) return;
    deleteInflight.current.add(uid);
    setDeletingUid(uid);

    try {
      const response = await authFetch(`${getApiBaseUrl()}/manage/deleteUser`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, confirmPhrase: 'DELETE THIS USER' }),
      });
      if (!response.ok) {
        let msg = `Delete failed (${response.status})`;
        try {
          const err = await response.json();
          const serverMsg = err.message || err.error;
          if (serverMsg) {
            msg = `${serverMsg} (${response.status})`;
          }
        } catch { /* non-JSON body */ }
        throw new Error(msg);
      }
      const data = await response.json();
      // Remove from local list
      setCustomers(prev => prev.filter(c => c.uid !== uid));
      setTotal(prev => Math.max(0, prev - 1));
      alert(`✅ User "${label}" permanently deleted. (${data.stats?.firestoreDocs ?? 0} docs removed)`);
    } catch (err) {
      alert(`❌ Failed to delete user: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      deleteInflight.current.delete(uid);
      setDeletingUid(null);
    }
  };

  const loadCustomers = useCallback(async (cursor?: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      params.append('limit', '25');
      if (cursor) params.append('cursor', cursor);
      if (search) params.append('search', search);
      if (stageFilter !== 'all') params.append('stage', stageFilter);

      const response = await authFetch(`${getApiBaseUrl()}/getCustomers?${params.toString()}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load customers');
      }

      if (cursor) {
        setCustomers(prev => [...prev, ...data.customers]);
      } else {
        setCustomers(data.customers);
      }
      
      setTotal(data.total);
      setNextCursor(data.nextCursor);
      setHasMore(data.hasMore);

      // Calculate segment stats
      if (!cursor && data.customers.length > 0) {
        const stats: Record<string, number> = {};
        data.customers.forEach((c: CustomerSummary) => {
          const stage = c.customerStage || 'visitor';
          stats[stage] = (stats[stage] || 0) + 1;
        });
        setSegmentStats(stats);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [search, stageFilter]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadCustomers();
  };

  // Format helpers
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return t('customers.dates.invalidDate');
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return t('customers.dates.invalidDate');
    return date.toLocaleDateString(language === 'ko' ? 'ko-KR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatRelativeDate = (dateStr: string | null): { text: string; className: string } => {
    if (!dateStr) return { text: t('customers.dates.never'), className: 'activity-dormant' };
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return { text: t('customers.dates.never'), className: 'activity-dormant' };
    
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return { text: t('customers.dates.today'), className: 'activity-recent' };
    if (diffDays === 1) return { text: t('customers.dates.yesterday'), className: 'activity-recent' };
    if (diffDays < 7) return { text: t('customers.dates.daysAgo', { days: diffDays }), className: 'activity-recent' };
    if (diffDays < 30) return { text: t('customers.dates.weeksAgo', { weeks: Math.floor(diffDays / 7) }), className: 'activity-stale' };
    return { text: t('customers.dates.monthsAgo', { months: Math.floor(diffDays / 30) }), className: 'activity-dormant' };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getProfileLevel = (completion: number): string => {
    if (completion >= 50) return 'high';
    if (completion >= 30) return 'medium';
    return 'low';
  };

  /** Normalize raw skin type from DB and return translated label */
  const getSkinTypeLabel = (raw: string | null): string => {
    if (!raw) return '-';
    const koreanMap: Record<string, string> = {
      '지성': 'oily', '건성': 'dry', '중성': 'normal',
      '복합성': 'combination', '민감성': 'sensitive',
    };
    const key = koreanMap[raw] || raw.toLowerCase();
    const translated = tc(`skinTypes.${key}`, { defaultValue: '' });
    return translated || raw;
  };

  const getStageBadge = (stage: string | null) => {
    const stageMap: Record<string, { labelKey: string; className: string }> = {
      'visitor': { labelKey: 'customers.stages.visitor', className: 'stage-visitor' },
      'sample-requested': { labelKey: 'customers.stages.sampleRequested', className: 'stage-engaged' },
      'sample-sent': { labelKey: 'customers.stages.sampleSent', className: 'stage-engaged' },
      'sample-delivered': { labelKey: 'customers.stages.sampleDelivered', className: 'stage-engaged' },
      'feedback-given': { labelKey: 'customers.stages.feedbackGiven', className: 'stage-purchaser' },
      'converted': { labelKey: 'customers.stages.converted', className: 'stage-repeat' },
      'loyal': { labelKey: 'customers.stages.loyal', className: 'stage-vip' },
      'churned': { labelKey: 'customers.stages.churned', className: 'stage-visitor' },
    };
    
    const info = stageMap[stage || 'visitor'] || { labelKey: stage || 'visitor', className: 'stage-visitor' };
    return <span className={`stage-badge ${info.className}`}>{t(info.labelKey)}</span>;
  };

  const getInitials = (name: string | null, email: string | null): string => {
    if (name) return name.charAt(0).toUpperCase();
    if (email) return email.charAt(0).toUpperCase();
    return '?';
  };

  // Segment icons
  const segmentIcons: Record<string, string> = {
    'visitor': '👀',
    'sample-requested': '📦',
    'sample-sent': '🚚',
    'sample-delivered': '✅',
    'feedback-given': '💬',
    'converted': '💳',
    'loyal': '⭐',
    'churned': '😴',
  };

  return (
    <div className="customers-tab">
      {/* Sub-tab Navigation */}
      <div className="subtab-navigation">
        <button 
          className={subTab === 'all' ? 'active' : ''} 
          onClick={() => { setSubTab('all'); }}
        >
          👥 {t('customers.allUsers')}
        </button>
        <button 
          className={subTab === 'segments' ? 'active' : ''} 
          onClick={() => setSubTab('segments')}
        >
          📊 {t('customers.segments')}
        </button>
      </div>

      {/* Search & Filters */}
      <div className="filters-bar">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder={t('customers.search.placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="submit">🔍</button>
        </form>

        {subTab === 'all' && (
          <select 
            value={stageFilter} 
            onChange={(e) => setStageFilter(e.target.value as StageFilter)}
            className="filter-select"
          >
            <option value="all">{t('customers.filters.allStages')}</option>
            <option value="visitor">{t('customers.stages.visitor')}</option>
            <option value="sample-requested">{t('customers.stages.sampleRequested', 'Sample Requested')}</option>
            <option value="feedback-given">{t('customers.stages.feedbackGiven', 'Feedback Given')}</option>
            <option value="converted">{t('customers.stages.converted', 'Converted')}</option>
            <option value="loyal">{t('customers.stages.loyal', 'Loyal')}</option>
            <option value="churned">{t('customers.stages.churned', 'Churned')}</option>
          </select>
        )}
      </div>

      {/* Stats Summary */}
      <div className="stats-summary">
        <div className="stat-item">
          <span className="stat-label">{t('customers.stats.total')}</span>
          <span className="stat-value">{total}</span>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="error-message">
          ⚠️ {error}
          <button onClick={() => loadCustomers()}>{t('customers.actions.retry')}</button>
        </div>
      )}

      {/* Loading State */}
      {loading && customers.length === 0 && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('customers.loading.customers')}</p>
        </div>
      )}

      {/* Segments View */}
      {subTab === 'segments' && !loading && (
        <div className="segments-grid">
          {Object.entries(segmentStats).map(([stage, count]) => (
            <div key={stage} className="segment-card">
              <div className="segment-icon">{segmentIcons[stage] || '👤'}</div>
              <div className="segment-count">{count}</div>
              <div className="segment-label">{stage.replace(/-/g, ' ')}</div>
              <div className="segment-percent">
                {total > 0 ? `${((count / total) * 100).toFixed(1)}%` : '0%'}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Customer List */}
      {subTab !== 'segments' && !loading && customers.length === 0 && (
        <div className="empty-state">
          <p>👥 {t('customers.empty.noCustomers')}</p>
        </div>
      )}

      {subTab !== 'segments' && customers.length > 0 && (
        <div className="customers-table-container">
          <table className="customers-table">
            <thead>
              <tr>
                <th>{t('customers.user')}</th>
                <th>{t('customers.stage')}</th>
                <th>{t('customers.skinType')}</th>
                <th>{t('customers.ltv')}</th>
                <th>{t('customers.profile')}</th>
                <th>{t('customers.lastActive')}</th>
                <th style={{ width: '50px', textAlign: 'center' }}>💬</th>
                <th style={{ width: '50px', textAlign: 'center' }}>🗑️</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => {
                const lastActive = formatRelativeDate(customer.lastVisitDate);
                const profileLevel = getProfileLevel(customer.profileCompletion);
                
                return (
                  <React.Fragment key={customer.uid}>
                    <tr
                      className={profileExpandedUid === customer.uid ? 'row-expanded' : ''}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setProfileExpandedUid(prev => prev === customer.uid ? null : customer.uid)}
                      tabIndex={0}
                      role="button"
                      aria-expanded={profileExpandedUid === customer.uid}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setProfileExpandedUid(prev => prev === customer.uid ? null : customer.uid);
                        }
                      }}
                    >
                      <td className="user-cell">
                        {customer.photoURL ? (
                          <img src={customer.photoURL} alt="" className="user-avatar" />
                        ) : (
                          <div className="user-avatar-placeholder">
                            {getInitials(customer.name, customer.email)}
                          </div>
                        )}
                        <div className="user-info">
                          <span className="user-name">{customer.name || t('customers.anonymous')}</span>
                          <span className="user-email">{customer.email || '-'}</span>
                        </div>
                      </td>
                      <td>{getStageBadge(customer.customerStage)}</td>
                      <td>
                        <span className="skin-type">{getSkinTypeLabel(customer.skinType)}</span>
                      </td>
                      <td>{formatCurrency(customer.lifetimeValueUSD)}</td>
                      <td>
                        <div className="profile-progress">
                          <div className="progress-bar-container">
                            <div 
                              className={`progress-bar-fill ${profileLevel}`}
                              style={{ width: `${Math.max(customer.profileCompletion, 5)}%` }}
                            />
                          </div>
                          <span>{customer.profileCompletion}%</span>
                        </div>
                      </td>
                      <td>
                        <div className="activity-cell">
                          <span className={`activity-primary ${lastActive.className}`}>
                            {lastActive.text}
                          </span>
                          <span className="activity-secondary">
                            {t('customers.joined')} {formatDate(customer.createdAt)}
                          </span>
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="admin-button admin-button--secondary"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                          onClick={(e) => { e.stopPropagation(); toggleUserChat(customer.uid); }}
                          title={t('customers.viewChatLogs', 'View chat logs for this user')}
                          aria-label={`View chat logs for ${customer.name || customer.email || t('customers.anonymous')}`}
                        >
                          {chatExpandedUid === customer.uid ? '🔽' : '💬'}
                        </button>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="admin-button admin-button--danger"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                          disabled={deletingUid === customer.uid}
                          onClick={(e) => { e.stopPropagation(); handleDeleteUser(customer.uid, customer.name, customer.email); }}
                          title="Permanently delete this user"
                          aria-label={`Permanently delete user ${customer.name || customer.email || t('customers.anonymous')}`}
                        >
                          {deletingUid === customer.uid ? '⏳' : '🗑️'}
                        </button>
                      </td>
                    </tr>
                    {/* Per-user profile detail expansion */}
                    {profileExpandedUid === customer.uid && (
                      <tr className="profile-expansion-row">
                        <td colSpan={8}>
                          <div className="user-profile-detail">
                            <div className="user-profile-detail__grid">
                              <div className="user-profile-detail__item">
                                <span className="user-profile-detail__label">{t('customers.skinType')}</span>
                                <span className="user-profile-detail__value">{getSkinTypeLabel(customer.skinType)}</span>
                              </div>
                              <div className="user-profile-detail__item">
                                <span className="user-profile-detail__label">{t('customers.concerns', 'Concerns')}</span>
                                <span className="user-profile-detail__value">{customer.concerns?.length ? customer.concerns.join(', ') : '—'}</span>
                              </div>
                              <div className="user-profile-detail__item">
                                <span className="user-profile-detail__label">{t('customers.stage')}</span>
                                <span className="user-profile-detail__value">{getStageBadge(customer.customerStage)}</span>
                              </div>
                              <div className="user-profile-detail__item">
                                <span className="user-profile-detail__label">{t('customers.ltv')}</span>
                                <span className="user-profile-detail__value">{formatCurrency(customer.lifetimeValueUSD)}</span>
                              </div>
                              <div className="user-profile-detail__item">
                                <span className="user-profile-detail__label">{t('customers.email', 'Email')}</span>
                                <span className="user-profile-detail__value">{customer.email || '—'}</span>
                              </div>
                              <div className="user-profile-detail__item">
                                <span className="user-profile-detail__label">UID</span>
                                <span className="user-profile-detail__value" style={{ fontSize: '11px', fontFamily: 'monospace' }}>{customer.uid}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    {/* Per-user chat log expansion */}
                    {chatExpandedUid === customer.uid && (
                      <tr className="chat-expansion-row">
                        <td colSpan={8}>
                          <div className="user-chat-panel">
                            <div className="user-chat-header">
                              <strong>💬 {t('customers.chatLogs', 'Chat Logs')} — {customer.name || customer.email || customer.uid.slice(0, 8)}</strong>
                            </div>
                            {chatLoading && (
                              <div className="user-chat-loading">
                                <div className="spinner" /> {t('customers.chatLoading', 'Loading conversations…')}
                              </div>
                            )}
                            {!chatLoading && chatError && (
                              <div className="user-chat-empty user-chat-error">
                                {chatError}
                                <button
                                  className="admin-button admin-button--secondary"
                                  style={{ marginLeft: 8, padding: '2px 8px', fontSize: '12px' }}
                                  onClick={() => toggleUserChat(customer.uid, true)}
                                >
                                  {t('customers.actions.retry')}
                                </button>
                              </div>
                            )}
                            {!chatLoading && !chatError && chatConversations.length === 0 && (
                              <div className="user-chat-empty">{t('customers.noChatConversations', 'No conversations found for this user.')}</div>
                            )}
                            {!chatLoading && chatConversations.length > 0 && (
                              <div className="user-chat-list">
                                {chatConversations.map((conv) => (
                                  <div key={conv.conversationId} className={`user-chat-conv ${chatExpandedConvId === conv.conversationId ? 'expanded' : ''}`}>
                                    <button
                                      className="user-chat-conv-header"
                                      onClick={() => setChatExpandedConvId(prev => prev === conv.conversationId ? null : conv.conversationId)}
                                    >
                                      <span className="user-chat-conv-time">{formatChatTimestamp(conv.lastMessageAt)}</span>
                                      <span className="user-chat-conv-count">{conv.messageCount} msgs</span>
                                      {conv.topics.length > 0 && (
                                        <span className="user-chat-conv-topics">
                                          {conv.topics.slice(0, 3).map(t => <span key={t} className="chat-topic-tag">{t}</span>)}
                                        </span>
                                      )}
                                      <span className="user-chat-conv-preview">
                                        {conv.userPreview ? (conv.userPreview.length > 80 ? conv.userPreview.slice(0, 80) + '…' : conv.userPreview) : '—'}
                                      </span>
                                      <span>{chatExpandedConvId === conv.conversationId ? '▲' : '▼'}</span>
                                    </button>
                                    {chatExpandedConvId === conv.conversationId && (
                                      <div className="user-chat-messages">
                                        {conv.messages.map((msg, idx) => (
                                          <div key={idx} className={`user-chat-msg ${msg.sender === 'user' ? 'user-chat-msg--user' : 'user-chat-msg--bot'}`}>
                                            <div className="user-chat-msg-meta">
                                              {msg.sender === 'user' ? '👤 User' : '🤖 AI'}
                                              <span className="user-chat-msg-time">{formatChatTimestamp(msg.timestamp)}</span>
                                            </div>
                                            <div className="user-chat-msg-text">{msg.text}</div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Load More */}
      {hasMore && subTab !== 'segments' && (
        <div className="load-more">
          <button 
            onClick={() => loadCustomers(nextCursor || undefined)}
            disabled={loading}
          >
            {loading ? t('customers.actions.loading') : t('customers.actions.loadMore')}
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomersTab;
